import { Button } from "components/DesignSystem/Button/Button";
import {
  CLICKED_ENQUIRE_ABOUT_FILING_ON_FILINGS,
  CLICKED_MARK_FILED_EXTERNALLY_ON_FILINGS,
  CLICKED_MARK_NOT_REQUIRED_ON_FILINGS,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import ThreeDots from "static/images/ThreeDots.svg";
import { stopPropagation } from "utils/stopPropagation";
import { MarkFilingAsNotRequired } from "./MarkFilingAsNotRequired";
import { ArchiveFiling } from "./ArchiveFiling";
import { ChangeTaskSeason } from "./ChangeTaskSeason";
import { TaskFiledExternally } from "./TaskFiledExternally";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHistory } from "react-router-dom";
import { openLink } from "utils/openLink";

export const TaskDeadlineOptions = ({
  taskDeadline,
  openSliderChat,
}: {
  taskDeadline: TaskDeadlineOverviewItem;
  openSliderChat: (taskId: string) => void;
}) => {
  const notRequired = useModal();
  const filedExternally = useModal();
  const archive = useModal();
  const changeSeason = useModal();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const { secondary_channel_url } = useCurrentGroupContext();
  const history = useHistory();
  const { trackEvent } = useAnalytics();

  const filingDetails = {
    filing_name: taskDeadline.task_title,
    is_launched: Boolean(taskDeadline.task_uuid),
  };

  // Check if any dropdown items should be rendered
  const hasNotRequiredOrFiledExternallyItems =
    (taskDeadline.source === "ALARMS" || taskDeadline.source === "CART") &&
    !taskDeadline.delete_reason;
  const hasArchiveItem = isAdmin && taskDeadline.task_uuid;

  const handleNotRequiredOpen = () => {
    trackEvent(CLICKED_MARK_NOT_REQUIRED_ON_FILINGS, filingDetails);
    notRequired.open();
  };

  const handleFiledExternallyOpen = () => {
    trackEvent(CLICKED_MARK_FILED_EXTERNALLY_ON_FILINGS, filingDetails);
    filedExternally.open();
  };

  const handleChatOpen = () => {
    trackEvent(CLICKED_ENQUIRE_ABOUT_FILING_ON_FILINGS, filingDetails);

    if (taskDeadline.channel?.channel_url) {
      openSliderChat(taskDeadline.task_uuid!);
    } else {
      openLink(`/chat/${secondary_channel_url ?? ""}`);
    }
  };

  // Return null if no dropdown items would be rendered
  if (!hasNotRequiredOrFiledExternallyItems && !isCustomer && !hasArchiveItem) {
    return null;
  }

  return (
    <div onClick={stopPropagation}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button size="xsmall" customType="ghost_icon">
            <img src={ThreeDots} alt="Action" className="t-select-none" />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end">
            {hasNotRequiredOrFiledExternallyItems && (
              <>
                <Dropdown.Item onSelect={handleNotRequiredOpen}>
                  Mark not required
                </Dropdown.Item>
                <Dropdown.Item onSelect={handleFiledExternallyOpen}>
                  Mark filed externally
                </Dropdown.Item>
              </>
            )}
            {isCustomer && (
              <Dropdown.Item onSelect={handleChatOpen}>
                Enquire about the filing
              </Dropdown.Item>
            )}
            {hasArchiveItem && (
              <>
                <Dropdown.Item onSelect={archive.open}>
                  {taskDeadline.is_archived ? "Unarchive" : "Archive"}
                </Dropdown.Item>
                <Dropdown.Item onSelect={changeSeason.open}>
                  Change season
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {notRequired.isOpen && (
        <MarkFilingAsNotRequired {...notRequired} taskDeadline={taskDeadline} />
      )}
      {archive.isOpen && (
        <ArchiveFiling {...archive} taskDeadline={taskDeadline} />
      )}
      {changeSeason.isOpen && (
        <ChangeTaskSeason {...changeSeason} taskDeadline={taskDeadline} />
      )}
      {filedExternally.isOpen && (
        <TaskFiledExternally {...filedExternally} taskDeadline={taskDeadline} />
      )}
    </div>
  );
};
